/**
 * Pet Parent Hub — Foster 30-Day Checklist content (dog + cat).
 *
 * Ported from the design handoff data.js (DOG/CAT). Task `id`s are the stable
 * `task_key`s persisted in checklist completion — do not rename them once shipped.
 * Every card links in-Hub to /hub/lessons-and-guides/<slug>; titles must match the
 * lesson catalog exactly (enforced by checklistData.test.ts). `{pet}` tokens are filled
 * with "your foster dog"/"your foster cat" by getFosterChecklist.
 */
import {
  AgeBracket, ChecklistData, ChecklistStageData, ChecklistTaskData, TINT, guide, lesson,
} from '../Home/checklistData';
import { fillPet } from '../Home/checklistUtils';

const LESSONS = '/hub/lessons-and-guides/';
const GUIDE_META = 'Guide · 6-minute read';
const THINKIFIC = 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2F';

const fureverHomeTask = (id: string): ChecklistTaskData => ({
  id,
  title: "Review 'Finding Your Foster a Furever Home'",
  summary: 'Your notes and photos help match {pet} with the right adopter.',
  guidance: [
    'Share what you’ve learned about {pet}’s personality and routine',
    'Take clear, flattering photos and short videos for the adoption listing',
    'Help prepare {pet} and the adopter for a smooth transition',
  ],
  cards: [guide(
    'Finding Your Foster A Furever Home',
    GUIDE_META,
    `${LESSONS}finding-your-foster-a-furever-home`,
    { image: '/lessons/finding-your-foster-a-furever-home.jpg', tint: TINT.pink, icon: 'menu_book' },
  )],
});

export const FOSTER_DOG_CHECKLIST: ChecklistData = {
  species: 'dog',
  stages: [
    {
      id: 'foster-dog-s1',
      range: 'Days 1–3',
      name: 'Decompression & Safety',
      short: 'Decompression',
      desc: 'Keep the house quiet during this phase. If you have other animals, keep them separated while {pet} adjusts.',
      tasks: [
        {
          id: 'foster-dog-1',
          title: "Review 'Overview of Dog Fostering'",
          summary: 'Learn what to expect as a foster and how to help your foster dog find their forever home.',
          guidance: [
            'Understand your role as a foster and what your organization will provide',
            'Prepare supplies and a plan for {pet} before pickup day',
            'Know who to contact for medical, behavioral, or logistical questions',
          ],
          cards: [guide(
            'Overview of Dog Fostering',
            GUIDE_META,
            `${LESSONS}overview-of-dog-fostering`,
            { image: '/lessons/overview-of-dog-fostering.jpg', tint: TINT.purple, icon: 'menu_book' },
          )],
        },
        {
          id: 'foster-dog-2',
          title: 'Help Your Foster Dog Get Settled',
          summary: 'The first days set the tone — go slow and let {pet} decompress.',
          guidance: [
            'Keep the first few days calm and limit visitors and outings',
            'Give {pet} space and let them approach you at their own pace',
            'Introduce resident pets slowly, at the pace of the most cautious animal',
          ],
          cards: [guide(
            'Getting Your Foster Dog Settled',
            GUIDE_META,
            `${LESSONS}getting-your-foster-dog-settled`,
            { image: '/lessons/getting-your-foster-dog-settled.jpg', tint: TINT.teal, icon: 'home' },
          )],
        },
        {
          id: 'foster-dog-3',
          title: 'Set up a safe space',
          summary: 'Create a quiet, comfortable area where your dog can decompress and feel secure.',
          guidance: [
            'Choose a quiet room or area away from foot traffic',
            'Add a crate, comfy bed, and water bowl',
            "Let {pet} explore at their own pace — don't force interaction",
          ],
          cards: [lesson(
            'Creating a Safe Space for Your Dog',
            '4-minute lesson',
            `${LESSONS}creating-a-safe-space-for-your-dog`,
            { image: `${THINKIFIC}NfFKnIRwRaWxQm5UO38M_Crate%20training%201.png`, tint: TINT.teal },
          )],
        },
        {
          id: 'foster-dog-4',
          title: 'Establish a Potty & Daily Routine',
          summary: 'A predictable rhythm of meals, potty breaks, rest, and play builds security and prevents accidents.',
          guidance: [
            'Follow a consistent schedule for meals, potty breaks, rest, and play',
            'Take {pet} outside frequently and reward potty successes',
            'Balance training, enrichment, and downtime to support a smooth adjustment',
          ],
          cards: [
            lesson(
              'Your Dog’s Daily Routine',
              '5-minute lesson',
              `${LESSONS}your-dogs-daily-routine`,
              {
                conditional: true,
                image: '/hub/checklist/harry-getting-settled.png',
                icon: 'restaurant',
                tint: TINT.amber,
                under6: { title: "Your Puppy's Daily Routine", url: `${LESSONS}your-puppys-daily-routine` },
                over6: { title: 'Your Dog’s Daily Routine', url: `${LESSONS}your-dogs-daily-routine` },
              },
            ),
            lesson(
              'House Training',
              '4-minute lesson',
              `${LESSONS}house-training`,
              { image: '/hub/checklist/house-training.png', tint: TINT.green },
            ),
          ],
        },
      ],
    },
    {
      id: 'foster-dog-s2',
      range: 'Days 4–14',
      name: 'Foundations',
      short: 'Foundations',
      desc: 'Build good habits and learn the basics that will help you and {pet} succeed together.',
      tasks: [
        {
          id: 'foster-dog-5',
          title: 'Learn the Training Basics',
          summary: 'Short, positive sessions build confidence and a shared language.',
          guidance: [
            'Learn how positive reinforcement helps {pet} understand what you want',
            'Find rewards {pet} loves, such as treats, toys, or praise',
            'Learn how to use a marker word like “Yes!” or a clicker to make training clearer',
          ],
          cards: [
            lesson(
              'Introduction to Positive Reinforcement Training',
              '4-minute lesson',
              `${LESSONS}introduction-to-positive-reinforcement-training`,
              { image: `${THINKIFIC}Z5J6muoSQUKRbE5arwjW_Screen%20Shot%202023-11-06%20at%203.13.30%20PM.png`, tint: TINT.green, icon: 'menu_book' },
            ),
            lesson(
              'Building Good Associations',
              '5-minute lesson',
              `${LESSONS}building-good-associations`,
              { image: `${THINKIFIC}yEwrR0ChRnayKnNbsVIo_Building%20good%20associations.png`, tint: TINT.teal, icon: 'favorite' },
            ),
            lesson(
              'Name Recognition',
              '3-minute lesson',
              `${LESSONS}name-recognition`,
              { image: `${THINKIFIC}si3qHvO4Thehi50kSmm7_Name.png`, tint: TINT.amber, icon: 'badge' },
            ),
          ],
        },
      ],
    },
    {
      id: 'foster-dog-s3',
      range: 'Days 15–30',
      name: 'Confidence & Connection',
      short: 'Stability',
      desc: 'Deepen your bond and celebrate how far you and your dog have come.',
      tasks: [
        {
          id: 'foster-dog-6',
          title: 'Continue Basic Training',
          summary: 'Keep practicing cues and add gentle real-world challenges.',
          guidance: [
            'Practice Touch, Sit, and Down in different rooms and locations',
            'Use high-value rewards when introducing new distractions',
            'Celebrate small improvements and build confidence through repetition',
          ],
          cards: [
            lesson(
              'Sit',
              '3-minute lesson',
              `${LESSONS}sit`,
              { image: `${THINKIFIC}DVipo7B9TZ2PXRzBQHtt_sit.png`, icon: 'pets', tint: TINT.purple },
            ),
            lesson(
              'Touch',
              '3-minute lesson',
              `${LESSONS}touch`,
              { image: `${THINKIFIC}JwWVBpRiSEygkSDGJAog_Touch_Lesson.png`, icon: 'touch_app', tint: TINT.amber },
            ),
            lesson(
              'Down',
              '5-minute lesson',
              `${LESSONS}down`,
              { image: `${THINKIFIC}dDFkQY9mRiaOS8mlW6Ci_Down.png`, icon: 'pets', tint: TINT.pink },
            ),
          ],
        },
        fureverHomeTask('foster-dog-7'),
      ],
    },
  ],
};

export const FOSTER_CAT_CHECKLIST: ChecklistData = {
  species: 'cat',
  stages: [
    {
      id: 'foster-cat-s1',
      range: 'Days 1–3',
      name: 'Decompression & Safety',
      short: 'Decompression',
      desc: 'Help {pet} settle into their new environment and feel safe enough to explore.',
      tasks: [
        {
          id: 'foster-cat-1',
          title: "Review 'Overview of Cat Fostering'",
          summary: 'Learn what to expect as a foster and how to help your foster cat find their forever home.',
          guidance: [
            'Understand your role as a foster and what your organization will provide',
            'Prepare supplies and a quiet room for {pet} before pickup day',
            'Know who to contact for medical, behavioral, or logistical questions',
          ],
          cards: [guide(
            'Overview of Cat Fostering',
            GUIDE_META,
            `${LESSONS}overview-of-cat-fostering`,
            { image: '/lessons/overview-of-cat-fostering.jpg', tint: TINT.purple, icon: 'menu_book' },
          )],
        },
        {
          id: 'foster-cat-2',
          ageOnly: 'over6',
          title: 'Help Your Foster Cat Get Settled',
          summary: 'Set up one quiet room and let {pet} decompress at their own pace.',
          guidance: [
            'Start {pet} in one small, quiet room with everything they need',
            'Give {pet} a hiding spot and let them come to you first',
            'Introduce the rest of the home and any resident pets gradually',
          ],
          cards: [guide(
            'Getting Your Foster Cat Settled',
            GUIDE_META,
            `${LESSONS}getting-your-foster-cat-settled`,
            { image: '/lessons/getting-your-foster-cat-settled.jpg', tint: TINT.teal, icon: 'home' },
          )],
        },
        {
          id: 'foster-cat-3',
          ageOnly: 'under6',
          title: 'Learn the Basics of Kitten Care',
          summary: 'Keep your foster kitten healthy, warm, and comfortable with age-appropriate feeding and care.',
          guidance: [
            'Learn what {pet} needs for feeding, warmth, and weight tracking at their age',
            'Handle and socialize kittens gently and often to build confidence',
            'If a nursing mom is in your care, give her a quiet space and let her lead',
          ],
          cards: [
            guide(
              'Caring for Foster Kittens',
              GUIDE_META,
              `${LESSONS}caring-for-foster-kittens`,
              { image: '/lessons/caring-for-foster-kittens.jpg', tint: TINT.amber, icon: 'menu_book' },
            ),
            guide(
              'Fostering Moms & Kittens',
              GUIDE_META,
              `${LESSONS}fostering-moms-and-kittens`,
              { image: '/lessons/fostering-moms-and-kittens.jpg', tint: TINT.pink, icon: 'menu_book' },
            ),
          ],
        },
        {
          id: 'foster-cat-4',
          // Literal wording from the ticket's acceptance criteria (not a {pet} template).
          title: "Set up and respect your cat's safe space",
          summary: 'Create a quiet, comfortable area where your cat can decompress and feel secure.',
          guidance: [
            'Choose a small, quiet room to start',
            'Add a litter box, food, water, and a hiding spot',
            'Let {pet} come to you — patience builds trust',
          ],
          cards: [
            lesson(
              'Creating a Safe Space for Your Cat',
              '4-minute lesson',
              `${LESSONS}creating-a-safe-space-for-your-cat`,
              { image: `${THINKIFIC}RdcPJlNSSC1ONkLQiogg_Safe%20Space%20Cat.png`, tint: TINT.teal },
            ),
            lesson(
              'Hiding',
              '3-minute lesson',
              `${LESSONS}hiding`,
              { image: `${THINKIFIC}B0UUnTPNRg6ssx3ytz7s_Cat%20Hiding.png`, icon: 'visibility_off', tint: TINT.purple },
            ),
          ],
        },
        {
          id: 'foster-cat-5',
          title: 'Implement a daily routine',
          summary: 'Regular meals in a calm spot help a new cat feel at home.',
          guidance: [
            'Feed, play, and interact at consistent times each day to help your cat feel secure',
            'Schedule short play sessions in the early morning and evening when cats are naturally most active',
            'Give your cat time to rest and explore at their own pace between activities',
          ],
          cards: [lesson(
            "Your Cat's Daily Routine",
            '5-minute lesson',
            `${LESSONS}your-cats-daily-routine`,
            {
              conditional: true,
              icon: 'restaurant',
              tint: TINT.amber,
              image: `${THINKIFIC}96zMXdHjTtqAW9yJ2dKl_Litter%20Box%20Issues.png`,
              under6: { title: "Your Kitten's Daily Routine", url: `${LESSONS}your-kittens-daily-routine` },
              over6: { title: "Your Cat's Daily Routine", url: `${LESSONS}your-cats-daily-routine` },
            },
          )],
        },
      ],
    },
    {
      id: 'foster-cat-s2',
      range: 'Days 4–14',
      name: 'Foundations',
      short: 'Foundations',
      desc: 'Build good habits and keep {pet}’s mind and body engaged.',
      tasks: [
        {
          id: 'foster-cat-6',
          title: 'Incorporate Daily Enrichment',
          summary: 'Play and enrichment keep an indoor cat happy and confident.',
          guidance: [
            'Play with your cat 2–3 times per day using interactive toys',
            'Provide scratching posts, cat trees, and other opportunities to climb, scratch, and explore',
            'Focus enrichment activities in the early morning and evening when cats are naturally most active',
          ],
          cards: [lesson(
            'Enrichment',
            '5-minute lesson',
            `${LESSONS}enrichment`,
            { image: 'https://umsousercontent.com/lib_QOzUiRhzXcXZiVKF/iwla24rm9eqhftyr.png', icon: 'extension', tint: TINT.green },
          )],
        },
      ],
    },
    {
      id: 'foster-cat-s3',
      range: 'Days 15–30',
      name: 'Confidence & Connection',
      short: 'Stability',
      desc: 'Deepen your bond and keep your cat’s mind and body engaged.',
      tasks: [fureverHomeTask('foster-cat-7')],
    },
  ],
};

/**
 * The foster checklist for a species, filtered to the pet's age bracket (tasks with
 * `ageOnly` for another bracket are dropped) with `{pet}` filled.
 */
export function getFosterChecklist(species: 'dog' | 'cat', ageBracket: AgeBracket): ChecklistData {
  const data = species === 'cat' ? FOSTER_CAT_CHECKLIST : FOSTER_DOG_CHECKLIST;
  const petName = species === 'cat' ? 'your foster cat' : 'your foster dog';
  const stages: ChecklistStageData[] = data.stages.map((stage) => ({
    ...stage,
    tasks: stage.tasks.filter((t) => !t.ageOnly || t.ageOnly === ageBracket),
  }));
  return fillPet({ ...data, stages }, petName);
}
